import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { ImportExportManager } from '../components/ImportExportManager';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Building2,
  Phone,
  MapPin,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Columns3,
} from 'lucide-react';

// ─── Status config ────────────────────────────────────────────────────────────
const LEAD_STATUSES = [
  { value: 'no_contactado', label: 'Sin contactar', dot: 'bg-gray-400',   bg: 'bg-gray-100 dark:bg-gray-800',        text: 'text-gray-600 dark:text-gray-300' },
  { value: 'en_proceso',    label: 'En proceso',    dot: 'bg-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300' },
  { value: 'trato_cerrado', label: 'Trato cerrado', dot: 'bg-green-500',  bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-800 dark:text-green-300' },
  { value: 'rechazado',     label: 'Rechazado',     dot: 'bg-red-500',    bg: 'bg-red-100 dark:bg-red-900/30',      text: 'text-red-800 dark:text-red-300' },
];

const PROSPECTO_STATUSES = [
  { value: 'no_aplica',   label: 'NO APLICA',   bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-800 dark:text-red-300' },
  { value: 'seguimiento', label: 'SEGUIMIENTO', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300' },
  { value: 'contrato',    label: 'CONTRATO',    bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-800 dark:text-blue-300' },
];

// Mapea valores de ESTATUS del Excel al enum de leads del CRM
// CONTRATO→trato_cerrado, SEGUIMIENTO→en_proceso, NO APLICA→rechazado, vacío→no_contactado
function normalizeLeadStatus(val) {
  const v = String(val || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (!v) return 'no_contactado';
  if (v === 'CONTRATO' || v === 'TRATO_CERRADO' || v === 'CERRADO' || v === 'GANADO') return 'trato_cerrado';
  if (v === 'SEGUIMIENTO' || v === 'EN_PROCESO' || v === 'PROCESO') return 'en_proceso';
  if (v === 'NO_APLICA' || v === 'RECHAZADO' || v === 'NO_CONTACTO') return 'rechazado';
  const direct = ['no_contactado', 'en_proceso', 'trato_cerrado', 'rechazado'];
  if (direct.includes(v.toLowerCase())) return v.toLowerCase();
  return 'no_contactado';
}

function normalizeProspectoStatus(val) {
  const v = String(val || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (v === 'NO_APLICA' || v === 'NO APLICA') return 'no_aplica';
  if (v === 'CONTRATO') return 'contrato';
  if (v === 'SEGUIMIENTO') return 'seguimiento';
  return val ? String(val).toLowerCase().replace(/\s+/g, '_') : 'seguimiento';
}

const OCCUPANCY_OPTIONS = ['Habitada', 'Rentada', 'Desocupada'];
const WATER_OPTIONS     = ['Al corriente', 'SIN contrato'];
const LIGHT_OPTIONS     = ['Al corriente', 'SIN contrato'];
const PREDIAL_OPTIONS   = ['Al corriente', 'Nunca se pagó'];
const CREDIT_OPTIONS    = ['Infonavit', 'Bancario'];

const LeadStatusBadge = ({ value }) => {
  const cfg = LEAD_STATUSES.find(s => s.value === value) || LEAD_STATUSES[0];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const ProspectoStatusBadge = ({ value }) => {
  const cfg = PROSPECTO_STATUSES.find(s => s.value === value) || PROSPECTO_STATUSES[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

const YesNoBadge = ({ value }) =>
  value ? (
    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Sí</span>
  ) : (
    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">No</span>
  );

// ─── Column definitions ───────────────────────────────────────────────────────
const defaultLeadsColumns = [
  { id: 'contact_id', label: 'ID Contacto',       visible: true,  order: 0 },
  { id: 'name',       label: 'Nombre',             visible: true,  order: 1 },
  { id: 'date',       label: 'Fecha',              visible: true,  order: 2 },
  { id: 'has_debt',   label: '¿Tiene adeudo?',     visible: true,  order: 3 },
  { id: 'occupancy',  label: 'Habitada/Rentada/Desocupada', visible: true, order: 4 },
  { id: 'infonavit',  label: '¿Crédito Infonavit?', visible: true, order: 5 },
  { id: 'colonia',    label: 'Colonia',            visible: true,  order: 6 },
  { id: 'phone',      label: 'Número de contacto', visible: true,  order: 7 },
  { id: 'status',     label: 'Estatus',            visible: true,  order: 8 },
];

const defaultProspectosColumns = [
  { id: 'num',              label: 'No.',               visible: true,  order: 0 },
  { id: 'date',             label: 'Fecha',              visible: true,  order: 1 },
  { id: 'name',             label: 'Nombre',             visible: true,  order: 2 },
  { id: 'phone',            label: 'Teléfono',           visible: true,  order: 3 },
  { id: 'estado',           label: 'Estado',             visible: true,  order: 4 },
  { id: 'municipio',        label: 'Municipio',          visible: true,  order: 5 },
  { id: 'colonia',          label: 'Colonia',            visible: true,  order: 6 },
  { id: 'comment',          label: 'Comentario',         visible: true,  order: 7 },
  { id: 'water',            label: 'Agua',               visible: true,  order: 8 },
  { id: 'light',            label: 'Luz',                visible: true,  order: 9 },
  { id: 'predial',          label: 'Predial',            visible: true,  order: 10 },
  { id: 'credit',           label: 'Crédito',            visible: true,  order: 11 },
  { id: 'status',           label: 'Estatus',            visible: true,  order: 12 },
  { id: 'rejection_reason', label: 'Razón de rechazo',  visible: false, order: 13 },
];

// ─── SortIcon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40 inline-block flex-shrink-0" />;
  return sortDir === 'asc'
    ? <ArrowUp className="h-3 w-3 ml-1 inline-block flex-shrink-0 text-blue-500" />
    : <ArrowDown className="h-3 w-3 ml-1 inline-block flex-shrink-0 text-blue-500" />;
};

// ─── AddColumnModal ───────────────────────────────────────────────────────────
const COLUMN_TYPES = [
  { value: 'texto',   label: 'Texto' },
  { value: 'numero',  label: 'Número' },
  { value: 'fecha',   label: 'Fecha' },
  { value: 'si_no',   label: 'Sí / No' },
];

const AddColumnModal = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [colName, setColName] = useState('');
  const [colType, setColType] = useState('texto');

  const handleSave = () => {
    const trimmed = colName.trim();
    if (!trimmed) { toast.error('Escribe un nombre para la columna'); return; }
    onAdd({ name: trimmed, type: colType });
    setColName('');
    setColType('texto');
    setOpen(false);
    toast.success(`Columna "${trimmed}" agregada`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Columns3 className="h-4 w-4 mr-2" />
          Agregar columna
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Agregar columna personalizada</DialogTitle>
          <DialogDescription>Define el nombre y tipo de dato de la nueva columna.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nombre de la columna</Label>
            <Input
              placeholder="Ej: Zona, Precio, Observación..."
              value={colName}
              onChange={e => setColName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>
          <div className="space-y-2">
            <Label>Tipo de dato</Label>
            <Select value={colType} onValueChange={setColType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {COLUMN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700">Agregar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Custom column cell renderer ──────────────────────────────────────────────
const CustomCell = ({ col, row }) => {
  const val = row.custom?.[col.id];
  if (val === undefined || val === null || val === '') return <span className="text-muted-foreground">—</span>;
  if (col.type === 'si_no') return <YesNoBadge value={val === true || val === 'true' || val === 'Sí' || val === 'si'} />;
  if (col.type === 'fecha') return <span>{formatDate(val)}</span>;
  return <span>{val}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// LEADS TAB
// ─────────────────────────────────────────────────────────────────────────────
const LeadsTab = ({ user }) => {
  const { canEdit, canDelete } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [columns, setColumns] = useState(defaultLeadsColumns);
  const [customCols, setCustomCols] = useState([]);
  const [formData, setFormData] = useState({
    contact_id: '', name: '', date: '', has_debt: false,
    occupancy: 'Desocupada', infonavit: false, colonia: '', phone: '', status: 'no_contactado',
  });

  const fetchRecords = useCallback(async () => {
    try {
      const [leadsRes, colsRes] = await Promise.all([
        axios.get(`${API}/inmuebles/leads`),
        axios.get(`${API}/inmuebles/custom-columns/inmuebles_leads`),
      ]);
      setRecords(leadsRes.data || []);
      const dbCols = colsRes.data || [];
      setCustomCols(dbCols);
      // merge DB custom cols into column definitions
      if (dbCols.length > 0) {
        setColumns(prev => {
          const existingIds = prev.map(c => c.id);
          const newCols = dbCols
            .filter(c => !existingIds.includes(c.id))
            .map((c, i) => ({ id: c.id, label: c.column_name, visible: true, order: prev.length + i }));
          return [...prev, ...newCols];
        });
      }
    } catch (err) {
      console.error('Error cargando leads inmobiliarios:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSort = (field) => {
    setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const filtered = records.filter(r =>
    !search || [r.name, r.colonia, r.phone, r.contact_id].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );
  const sorted = [...filtered].sort((a, b) => {
    const va = (a[sortField] ?? '').toString();
    const vb = (b[sortField] ?? '').toString();
    return sortDir === 'asc' ? va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' })
                             : vb.localeCompare(va, undefined, { numeric: true, sensitivity: 'base' });
  });

  const visibleCols = [...columns].sort((a, b) => a.order - b.order).filter(c => c.visible);

  const resetForm = () => {
    setEditing(null);
    setFormData({ contact_id: '', name: '', date: '', has_debt: false, occupancy: 'Desocupada', infonavit: false, colonia: '', phone: '', status: 'no_contactado' });
  };

  const handleEdit = (rec) => {
    setEditing(rec);
    setFormData({ contact_id: rec.contact_id || '', name: rec.name || '', date: rec.date || '', has_debt: rec.has_debt || false, occupancy: rec.occupancy || 'Desocupada', infonavit: rec.infonavit || false, colonia: rec.colonia || '', phone: rec.phone || '', status: rec.status || 'no_contactado' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const res = await axios.put(`${API}/inmuebles/leads/${editing.id}`, formData);
        setRecords(prev => prev.map(r => r.id === editing.id ? res.data : r));
        toast.success('Lead actualizado');
      } else {
        const res = await axios.post(`${API}/inmuebles/leads`, formData);
        setRecords(prev => [res.data, ...prev]);
        toast.success('Lead registrado');
      }
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este lead?')) return;
    try {
      await axios.delete(`${API}/inmuebles/leads/${id}`);
      setRecords(prev => prev.filter(r => r.id !== id));
      toast.success('Lead eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleAddCustomCol = async ({ name, type }) => {
    try {
      const res = await axios.post(`${API}/inmuebles/custom-columns`, {
        tab_name: 'inmuebles_leads',
        column_name: name,
        column_type: type,
      });
      const newDbCol = res.data;
      setCustomCols(prev => [...prev, newDbCol]);
      setColumns(prev => [...prev, { id: newDbCol.id, label: newDbCol.column_name, visible: true, order: prev.length }]);
      toast.success(`Columna "${name}" agregada`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar columna');
    }
  };

  const total     = records.length;
  const cerrados  = records.filter(r => r.status === 'trato_cerrado').length;
  const enProceso = records.filter(r => r.status === 'en_proceso').length;

  if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground">Cargando leads...</div>;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total leads</p><p className="text-2xl font-bold">{total}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">En proceso</p><p className="text-2xl font-bold text-yellow-600">{enProceso}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Tratos cerrados</p><p className="text-2xl font-bold text-green-600">{cerrados}</p></CardContent></Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, colonia, teléfono..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <ColumnCustomizer columns={columns} storageKey="inmuebles_leads" onColumnsChange={setColumns} />
          <AddColumnModal onAdd={handleAddCustomCol} />
          <ImportExportManager
            module="inmuebles_leads"
            data={records}
            onImport={async (rows) => {
              let created = 0;
              for (const r of rows) {
                try {
                  const res = await axios.post(`${API}/inmuebles/leads`, {
                    contact_id: r.contact_id || r['ID Contacto'] || '',
                    name: r.name || r['Nombre'] || '',
                    date: r.date || r['Fecha'] || new Date().toISOString().split('T')[0],
                    has_debt: ['true','si','sí','1'].includes(String(r.has_debt ?? r['Tiene adeudo'] ?? '').toLowerCase()),
                    occupancy: r.occupancy || r['Estado'] || 'Desocupada',
                    infonavit: ['true','si','sí','1'].includes(String(r.infonavit ?? r['Infonavit'] ?? '').toLowerCase()),
                    colonia: r.colonia || r['Colonia'] || '',
                    phone: r.phone || r['Teléfono'] || r['Numero'] || '',
                    status: normalizeLeadStatus(r.status),
                  });
                  setRecords(prev => [res.data, ...prev]);
                  created++;
                } catch {}
              }
              toast.success(`${created} lead(s) importado(s)`);
            }}
          />
          <Dialog open={dialogOpen} onOpenChange={open => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-4 w-4 mr-2" /> Nuevo lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? 'Editar lead' : 'Nuevo lead'}</DialogTitle>
                <DialogDescription>{editing ? 'Modifica los datos del lead' : 'Registra un nuevo lead inmobiliario'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ID Contacto</Label>
                    <Input value={formData.contact_id} onChange={e => setFormData(p => ({ ...p, contact_id: e.target.value }))} placeholder="C-001" />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha</Label>
                    <Input type="date" value={formData.date} onChange={e => setFormData(p => ({ ...p, date: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Nombre *</Label>
                    <Input required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Nombre completo" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Número de contacto *</Label>
                    <Input required value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="55 1234 5678" />
                  </div>
                  <div className="space-y-2">
                    <Label>Colonia</Label>
                    <Input value={formData.colonia} onChange={e => setFormData(p => ({ ...p, colonia: e.target.value }))} placeholder="Ej: Roma Norte" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>¿Tiene adeudo?</Label>
                    <Select value={formData.has_debt ? 'si' : 'no'} onValueChange={v => setFormData(p => ({ ...p, has_debt: v === 'si' }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="si">Sí</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estado de la propiedad</Label>
                    <Select value={formData.occupancy} onValueChange={v => setFormData(p => ({ ...p, occupancy: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OCCUPANCY_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>¿Crédito Infonavit?</Label>
                    <Select value={formData.infonavit ? 'si' : 'no'} onValueChange={v => setFormData(p => ({ ...p, infonavit: v === 'si' }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="si">Sí</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estatus</Label>
                    <Select value={formData.status} onValueChange={v => setFormData(p => ({ ...p, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700">{editing ? 'Guardar cambios' : 'Registrar'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleCols.map(col => {
                    const sortable = ['contact_id','name','date','colonia','phone','status','occupancy'].includes(col.id);
                    const centered = ['has_debt','infonavit','occupancy'].includes(col.id);
                    return (
                      <TableHead
                        key={col.id}
                        className={`whitespace-nowrap ${sortable ? 'cursor-pointer select-none hover:bg-muted/30' : ''} ${centered ? 'text-center' : ''}`}
                        onClick={sortable ? () => handleSort(col.id) : undefined}
                      >
                        <div className={`flex items-center gap-1 ${centered ? 'justify-center' : ''}`}>
                          {col.label}
                          {sortable && <SortIcon field={col.id} sortField={sortField} sortDir={sortDir} />}
                        </div>
                      </TableHead>
                    );
                  })}
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleCols.length + 1} className="text-center py-10">
                      <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No hay leads registrados</p>
                    </TableCell>
                  </TableRow>
                ) : sorted.map((rec) => (
                  <TableRow key={rec.id} className="data-row">
                    {visibleCols.map(col => {
                      if (col.id === 'contact_id') return <TableCell key={col.id} className="font-mono text-xs text-muted-foreground">{rec.contact_id || '—'}</TableCell>;
                      if (col.id === 'name')       return <TableCell key={col.id} className="font-medium">{rec.name}</TableCell>;
                      if (col.id === 'date')       return <TableCell key={col.id} className="text-muted-foreground whitespace-nowrap">{formatDate(rec.date)}</TableCell>;
                      if (col.id === 'has_debt')   return <TableCell key={col.id} className="text-center"><YesNoBadge value={rec.has_debt} /></TableCell>;
                      if (col.id === 'occupancy')  return <TableCell key={col.id} className="text-center"><Badge variant="outline">{rec.occupancy}</Badge></TableCell>;
                      if (col.id === 'infonavit')  return <TableCell key={col.id} className="text-center"><YesNoBadge value={rec.infonavit} /></TableCell>;
                      if (col.id === 'colonia')    return <TableCell key={col.id}><div className="flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" />{rec.colonia || '—'}</div></TableCell>;
                      if (col.id === 'phone')      return <TableCell key={col.id}><div className="flex items-center gap-1"><Phone className="h-3 w-3 text-muted-foreground" />{rec.phone}</div></TableCell>;
                      if (col.id === 'status')     return <TableCell key={col.id}><LeadStatusBadge value={rec.status} /></TableCell>;
                      // custom columns
                      const customDef = customCols.find(c => c.id === col.id);
                      if (customDef) return <TableCell key={col.id}><CustomCell col={customDef} row={rec} /></TableCell>;
                      return <TableCell key={col.id}>—</TableCell>;
                    })}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && <DropdownMenuItem onClick={() => handleEdit(rec)}><Edit className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>}
                          {canDelete && <DropdownMenuItem onClick={() => handleDelete(rec.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PROSPECTOS EN SEGUIMIENTO TAB
// ─────────────────────────────────────────────────────────────────────────────
const ProspectosTab = ({ user }) => {
  const { canEdit, canDelete } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('num');
  const [sortDir, setSortDir] = useState('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [columns, setColumns] = useState(defaultProspectosColumns);
  const [customCols, setCustomCols] = useState([]);
  const emptyForm = { num: '', date: '', name: '', phone: '', estado: '', municipio: '', colonia: '', comment: '', water: 'Al corriente', light: 'Al corriente', predial: 'Al corriente', credit: 'Infonavit', status: 'seguimiento', rejection_reason: '' };
  const [formData, setFormData] = useState(emptyForm);

  const fetchRecords = useCallback(async () => {
    try {
      const [prosRes, colsRes] = await Promise.all([
        axios.get(`${API}/inmuebles/prospectos`),
        axios.get(`${API}/inmuebles/custom-columns/inmuebles_prospectos`),
      ]);
      setRecords(prosRes.data || []);
      const dbCols = colsRes.data || [];
      setCustomCols(dbCols);
      if (dbCols.length > 0) {
        setColumns(prev => {
          const existingIds = prev.map(c => c.id);
          const newCols = dbCols
            .filter(c => !existingIds.includes(c.id))
            .map((c, i) => ({ id: c.id, label: c.column_name, visible: true, order: prev.length + i }));
          return [...prev, ...newCols];
        });
      }
    } catch (err) {
      console.error('Error cargando prospectos inmobiliarios:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSort = (field) => {
    setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const filtered = records.filter(r =>
    !search || [r.name, r.colonia, r.phone, r.municipio, r.estado].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );
  const sorted = [...filtered].sort((a, b) => {
    const va = (a[sortField] ?? '').toString();
    const vb = (b[sortField] ?? '').toString();
    return sortDir === 'asc' ? va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' })
                             : vb.localeCompare(va, undefined, { numeric: true, sensitivity: 'base' });
  });

  const visibleCols = [...columns].sort((a, b) => a.order - b.order).filter(c => c.visible);

  const resetForm = () => { setEditing(null); setFormData(emptyForm); };

  const handleEdit = (rec) => {
    setEditing(rec);
    setFormData({ num: rec.num || '', date: rec.date || '', name: rec.name || '', phone: rec.phone || '', estado: rec.estado || '', municipio: rec.municipio || '', colonia: rec.colonia || '', comment: rec.comment || '', water: rec.water || 'Al corriente', light: rec.light || 'Al corriente', predial: rec.predial || 'Al corriente', credit: rec.credit || 'Infonavit', status: rec.status || 'seguimiento', rejection_reason: rec.rejection_reason || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const res = await axios.put(`${API}/inmuebles/prospectos/${editing.id}`, formData);
        setRecords(prev => prev.map(r => r.id === editing.id ? res.data : r));
        toast.success('Prospecto actualizado');
      } else {
        const res = await axios.post(`${API}/inmuebles/prospectos`, formData);
        setRecords(prev => [...prev, res.data]);
        toast.success('Prospecto registrado');
      }
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este prospecto?')) return;
    try {
      await axios.delete(`${API}/inmuebles/prospectos/${id}`);
      setRecords(prev => prev.filter(r => r.id !== id));
      toast.success('Prospecto eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleAddCustomCol = async ({ name, type }) => {
    try {
      const res = await axios.post(`${API}/inmuebles/custom-columns`, {
        tab_name: 'inmuebles_prospectos',
        column_name: name,
        column_type: type,
      });
      const newDbCol = res.data;
      setCustomCols(prev => [...prev, newDbCol]);
      setColumns(prev => [...prev, { id: newDbCol.id, label: newDbCol.column_name, visible: true, order: prev.length }]);
      toast.success(`Columna "${name}" agregada`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar columna');
    }
  };

  const total       = records.length;
  const seguimiento = records.filter(r => r.status === 'seguimiento').length;
  const noAplica    = records.filter(r => r.status === 'no_aplica').length;

  if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground">Cargando prospectos...</div>;

  const ServiceBadge = ({ value, options }) => {
    const isOk = value === options[0];
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${isOk ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
        {value || '—'}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total prospectos</p><p className="text-2xl font-bold">{total}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">En seguimiento</p><p className="text-2xl font-bold text-green-600">{seguimiento}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">No aplica</p><p className="text-2xl font-bold text-red-600">{noAplica}</p></CardContent></Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, colonia, municipio..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <ColumnCustomizer columns={columns} storageKey="inmuebles_prospectos" onColumnsChange={setColumns} />
          <AddColumnModal onAdd={handleAddCustomCol} />
          <ImportExportManager
            module="inmuebles_prospectos"
            data={records}
            onImport={async (rows) => {
              let created = 0;
              for (const r of rows) {
                try {
                  const res = await axios.post(`${API}/inmuebles/prospectos`, {
                    num: r.num ? parseInt(r.num) || undefined : undefined,
                    date: r.date || r['Fecha'] || new Date().toISOString().split('T')[0],
                    name: r.name || r['Nombre'] || '',
                    phone: r.phone || r['Teléfono'] || '',
                    estado: r.estado || r['Estado'] || '',
                    municipio: r.municipio || r['Municipio'] || '',
                    colonia: r.colonia || r['Colonia'] || '',
                    comment: r.comment || r['Comentario'] || '',
                    water: r.water || r['Agua'] || 'Al corriente',
                    light: r.light || r['Luz'] || 'Al corriente',
                    predial: r.predial || r['Predial'] || 'Al corriente',
                    credit: r.credit || r['Crédito'] || 'Infonavit',
                    status: normalizeProspectoStatus(r.status || r['Estatus'] || 'seguimiento'),
                    rejection_reason: r.rejection_reason || r['Razón de rechazo'] || r['RAZON RECHAZO'] || '',
                  });
                  setRecords(prev => [...prev, res.data]);
                  created++;
                } catch {}
              }
              toast.success(`${created} prospecto(s) importado(s)`);
            }}
          />
          <Dialog open={dialogOpen} onOpenChange={open => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-4 w-4 mr-2" /> Nuevo prospecto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? 'Editar prospecto' : 'Nuevo prospecto en seguimiento'}</DialogTitle>
                <DialogDescription>Registra los datos del prospecto y servicios de la propiedad.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Nombre completo" />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="55 1234 5678" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha</Label>
                    <Input type="date" value={formData.date} onChange={e => setFormData(p => ({ ...p, date: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <Input value={formData.estado} onChange={e => setFormData(p => ({ ...p, estado: e.target.value }))} placeholder="Ej: CDMX, Jalisco..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Municipio</Label>
                    <Input value={formData.municipio} onChange={e => setFormData(p => ({ ...p, municipio: e.target.value }))} placeholder="Ej: Benito Juárez" />
                  </div>
                  <div className="space-y-2">
                    <Label>Colonia</Label>
                    <Input value={formData.colonia} onChange={e => setFormData(p => ({ ...p, colonia: e.target.value }))} placeholder="Ej: Narvarte" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Comentario</Label>
                  <Textarea value={formData.comment} onChange={e => setFormData(p => ({ ...p, comment: e.target.value }))} rows={2} placeholder="Observaciones..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Agua</Label>
                    <Select value={formData.water} onValueChange={v => setFormData(p => ({ ...p, water: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{WATER_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Luz</Label>
                    <Select value={formData.light} onValueChange={v => setFormData(p => ({ ...p, light: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LIGHT_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Predial</Label>
                    <Select value={formData.predial} onValueChange={v => setFormData(p => ({ ...p, predial: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PREDIAL_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Crédito</Label>
                    <Select value={formData.credit} onValueChange={v => setFormData(p => ({ ...p, credit: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CREDIT_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Estatus</Label>
                    <Select value={formData.status} onValueChange={v => setFormData(p => ({ ...p, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PROSPECTO_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Razón de rechazo</Label>
                    <Input value={formData.rejection_reason} onChange={e => setFormData(p => ({ ...p, rejection_reason: e.target.value }))} placeholder="Solo si aplica..." />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700">{editing ? 'Guardar cambios' : 'Registrar'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleCols.map(col => {
                    const sortable = ['num','date','name','phone','estado','municipio','colonia','status'].includes(col.id);
                    return (
                      <TableHead
                        key={col.id}
                        className={`whitespace-nowrap ${sortable ? 'cursor-pointer select-none hover:bg-muted/30' : ''}`}
                        onClick={sortable ? () => handleSort(col.id) : undefined}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {sortable && <SortIcon field={col.id} sortField={sortField} sortDir={sortDir} />}
                        </div>
                      </TableHead>
                    );
                  })}
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleCols.length + 1} className="text-center py-10">
                      <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No hay prospectos registrados</p>
                    </TableCell>
                  </TableRow>
                ) : sorted.map((rec) => (
                  <TableRow key={rec.id} className="data-row">
                    {visibleCols.map(col => {
                      if (col.id === 'num')              return <TableCell key={col.id} className="font-mono text-xs text-muted-foreground">{rec.num}</TableCell>;
                      if (col.id === 'date')             return <TableCell key={col.id} className="text-muted-foreground whitespace-nowrap">{formatDate(rec.date)}</TableCell>;
                      if (col.id === 'name')             return <TableCell key={col.id} className="font-medium">{rec.name}</TableCell>;
                      if (col.id === 'phone')            return <TableCell key={col.id}><div className="flex items-center gap-1"><Phone className="h-3 w-3 text-muted-foreground" />{rec.phone || '—'}</div></TableCell>;
                      if (col.id === 'estado')           return <TableCell key={col.id}>{rec.estado || '—'}</TableCell>;
                      if (col.id === 'municipio')        return <TableCell key={col.id}>{rec.municipio || '—'}</TableCell>;
                      if (col.id === 'colonia')          return <TableCell key={col.id}><div className="flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" />{rec.colonia || '—'}</div></TableCell>;
                      if (col.id === 'comment')          return <TableCell key={col.id} className="max-w-[180px]"><span className="block truncate" title={rec.comment}>{rec.comment || '—'}</span></TableCell>;
                      if (col.id === 'water')            return <TableCell key={col.id}><ServiceBadge value={rec.water} options={WATER_OPTIONS} /></TableCell>;
                      if (col.id === 'light')            return <TableCell key={col.id}><ServiceBadge value={rec.light} options={LIGHT_OPTIONS} /></TableCell>;
                      if (col.id === 'predial')          return <TableCell key={col.id}><ServiceBadge value={rec.predial} options={PREDIAL_OPTIONS} /></TableCell>;
                      if (col.id === 'credit')           return <TableCell key={col.id}><Badge variant="outline" className="text-xs">{rec.credit || '—'}</Badge></TableCell>;
                      if (col.id === 'status')           return <TableCell key={col.id}><ProspectoStatusBadge value={rec.status} /></TableCell>;
                      if (col.id === 'rejection_reason') return <TableCell key={col.id} className="max-w-[160px]"><span className="block truncate text-sm text-muted-foreground" title={rec.rejection_reason}>{rec.rejection_reason || '—'}</span></TableCell>;
                      const customDef = customCols.find(c => c.id === col.id);
                      if (customDef) return <TableCell key={col.id}><CustomCell col={customDef} row={rec} /></TableCell>;
                      return <TableCell key={col.id}>—</TableCell>;
                    })}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && <DropdownMenuItem onClick={() => handleEdit(rec)}><Edit className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>}
                          {canDelete && <DropdownMenuItem onClick={() => handleDelete(rec.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const InmueblesPage = () => {
  const { user, isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Acceso Restringido</h2>
            <p className="text-muted-foreground">El módulo de Soluciones Inmobiliarias está disponible solo para administradores.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inmuebles-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Building2 className="h-8 w-8 text-amber-600" />
          Soluciones Inmobiliarias
        </h1>
        <p className="text-muted-foreground">Gestión de leads y prospectos — Bienes Raíces</p>
      </div>

      <Tabs defaultValue="leads">
        <TabsList className="mb-4">
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="prospectos">Prospectos en Seguimiento</TabsTrigger>
        </TabsList>

        <TabsContent value="leads">
          <LeadsTab user={user} />
        </TabsContent>

        <TabsContent value="prospectos">
          <ProspectosTab user={user} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
